import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import styles from './styles.module.css'
import { setBiometricApplicantId, setLoggedInLoadingMessageKey, useAppDispatch, useAppRouter } from '../../../state'
import { DEFAULT_LOCALE, Identity, IDENTIY_LOCALE, CompleteData, OnfidoError, OnfidoIdentityDocuments } from '@afterpaytouch/identity'
import { useBiometricHardId } from '../hooks'
import { IdentityDocument } from '@afterpay/types'
import { Loader, useToast } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { Route } from '../../../config/router'
import { app } from '../../../env'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'

export const BiometricHardId: FunctionComponent<{ document: IdentityDocument }> = ({ document }) => {
  const router = useAppRouter()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { locale: appLocale } = router
  const locale = IDENTIY_LOCALE[appLocale] ?? DEFAULT_LOCALE
  const [loading, setLoading] = useState(true)
  const { sdkToken, applicantId, isCreateApplicantError } = useBiometricHardId()
  const shouldRender = typeof sdkToken !== 'undefined' && typeof applicantId !== 'undefined'
  const setToast = useToast()
  const { t } = useTranslation()

  const handleOnComplete = (data: CompleteData): void => {
    if (typeof data !== 'undefined') {
      dispatch(setLoggedInLoadingMessageKey('verifyIdentity'))
      router.push(Route.VERIFYING_BIOMETRIC)
    }
  }
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isCreateApplicantError) {
      logEvent(TrackingEvent.VIEWED_ONFIDO_VERIFICATION_ERROR_TOAST, { errorMessage: 'create applicant error' })
      handleOnError()
    }
  }, [isCreateApplicantError])

  const handleOnError = (): void => {
    setToast({
      message: t('verifyIdentity:error:toast'),
      kind: 'Failure',
    })
    router.push(Route.VERIFY_IDENTITY)
  }

  const handleOnfidoError = (error: OnfidoError): void => {
    logEvent(TrackingEvent.VIEWED_ONFIDO_VERIFICATION_ERROR_TOAST, { type: error.type, message: error.message })
    handleOnError()
  }

  const handleOnEvent = (eventName: string): void => {
    if (eventName) {
      logEvent(TrackingEvent.VIEWED_ONFIDO_VERIFICATION_EVENT, { eventName })
    }
  }

  const handleOnLoad = (): void => {
    setLoading(false)
  }

  useEffect(() => {
    if (applicantId !== undefined) {
      dispatch(setBiometricApplicantId(applicantId))
    }
  }, [applicantId])

  const onfidoDocType = document === 'licence' ? OnfidoIdentityDocuments.LICENCE : OnfidoIdentityDocuments.PASSPORT

  const renderLoading = (): ReactNode => {
    return (
      <div className={styles.loadingText}>
        <Loader testNameSpace='biometric-hard-id' textProps={{ size: 'L', bold: true }} text={t('verifyIdentity:start:loading')} />
      </div>
    )
  }

  if (!shouldRender) {
    return (
      <div className={styles.loadingText}>
        <Loader testNameSpace='biometric-hard-id' textProps={{ size: 'L', bold: true }} text={t('verifyIdentity:start:loading')} />
      </div>
    )
  }
  return (
    <>
      {loading && renderLoading()}
      <Identity
        onLoad={handleOnLoad}
        onEvent={handleOnEvent}
        locale={locale}
        document={onfidoDocType}
        token={sdkToken}
        onComplete={handleOnComplete}
        onError={handleOnfidoError}
        env={app.APP_ENVIRONMENT}
      />
    </>
  )
}
